import { useEffect, useMemo, useState } from 'react';
import {
  Link2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  Copy,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { domainApi } from '@/lib/api';
import {
  PROXY_HOSTS,
  PROXY_PATHS,
  type ProxyHostKind,
  renderSnippet,
} from '@/lib/proxySnippets';

interface ApexProxySettingsProps {
  domainId: string;
  subdomain: string;
  isVerified: boolean;
}

interface VerifyResultRow {
  path: string;
  probedPath: string;
  ok: boolean;
  reason?: string;
}

export function ApexProxySettings({ domainId, subdomain, isVerified }: ApexProxySettingsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [hostKind, setHostKind] = useState<ProxyHostKind | ''>('');
  const [apexDomain, setApexDomain] = useState('');
  const [enabledPaths, setEnabledPaths] = useState<string[]>(
    PROXY_PATHS.filter((p) => p.defaultOn).map((p) => p.path)
  );
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);
  const [verifyResults, setVerifyResults] = useState<VerifyResultRow[] | null>(null);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await domainApi.getProxy(domainId);
      setHostKind((res.proxy.hostKind as ProxyHostKind) || '');
      setApexDomain(res.proxy.apexDomain || '');
      if (res.proxy.pathsEnabled.length > 0) {
        setEnabledPaths(res.proxy.pathsEnabled);
      }
      setVerifiedAt(res.proxy.verifiedAt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load proxy config');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isVerified && isExpanded) {
      load();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId, isVerified, isExpanded]);

  const togglePath = (path: string) => {
    setEnabledPaths((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
    setVerifyResults(null);
    setVerifiedAt(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await domainApi.saveProxy(domainId, {
        hostKind: hostKind || null,
        apexDomain: apexDomain.trim() || null,
        pathsEnabled: enabledPaths,
      });
      setSuccess('Proxy configuration saved. Run Verify after deploying the snippet.');
      setVerifiedAt(null);
      setVerifyResults(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleVerify = async () => {
    if (!apexDomain.trim()) {
      setError('Enter your apex domain (e.g., example.com) before verifying.');
      return;
    }
    if (enabledPaths.length === 0) {
      setError('Enable at least one path before verifying.');
      return;
    }
    setIsVerifying(true);
    setError(null);
    setSuccess(null);
    setVerifyResults(null);
    try {
      const res = await domainApi.verifyProxy(domainId);
      setVerifyResults(res.results);
      if (res.verified) {
        setVerifiedAt(res.verifiedAt);
        setSuccess('Apex proxy verified — every enabled path serves the SitemapHost body.');
      } else {
        setError('Some paths did not verify. See per-path status below.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verify failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const snippet = useMemo(() => {
    if (!hostKind) return '';
    return renderSnippet(hostKind, subdomain, enabledPaths);
  }, [hostKind, subdomain, enabledPaths]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setSuccess('Snippet copied to clipboard.');
      window.setTimeout(() => setSuccess((s) => (s === 'Snippet copied to clipboard.' ? null : s)), 2000);
    } catch {
      setError('Clipboard copy failed — select the text manually.');
    }
  };

  const hostInfo = hostKind ? PROXY_HOSTS[hostKind] : null;
  const isConfigured = !!hostKind && !!apexDomain.trim();

  return (
    <div className="bg-white rounded-xl border shadow-sm">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 sm:p-6 text-left"
      >
        <div className="flex items-center gap-3">
          <Link2 className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="text-base sm:text-lg font-semibold">Connect your apex domain</h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              {isConfigured
                ? verifiedAt
                  ? 'Verified — well-known files serve from your apex.'
                  : 'Configured — run Verify after deploying the snippet.'
                : 'Mount /llms.txt and /.well-known/* on your apex domain via a one-time snippet.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {verifiedAt && (
            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5" />
              Verified
            </span>
          )}
          <ChevronDown className={cn('w-5 h-5 text-slate-400 transition-transform', isExpanded && 'rotate-180')} />
        </div>
      </button>

      {isExpanded && (
        <div className="border-t p-4 sm:p-6 space-y-5">
          {!isVerified && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Verify your domain (CNAME + SSL) before setting up the apex proxy.</span>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium mb-1.5">Your main-site host</label>
                <select
                  value={hostKind}
                  onChange={(e) => setHostKind(e.target.value as ProxyHostKind | '')}
                  disabled={!isVerified}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-slate-50"
                >
                  <option value="">Select your host…</option>
                  {Object.values(PROXY_HOSTS).map((h) => (
                    <option key={h.kind} value={h.kind}>
                      {h.label}
                    </option>
                  ))}
                </select>
                {hostInfo && (
                  <p className="text-xs text-slate-500 mt-1">
                    {hostInfo.description} · paste into <code className="font-mono">{hostInfo.filename}</code>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">Your apex domain</label>
                <input
                  type="text"
                  value={apexDomain}
                  onChange={(e) => setApexDomain(e.target.value)}
                  disabled={!isVerified}
                  placeholder="example.com"
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-slate-50"
                />
                <p className="text-xs text-slate-500 mt-1">
                  The domain agents look up — e.g., <code className="font-mono">example.com</code> when your sitemap subdomain is{' '}
                  <code className="font-mono">{subdomain}</code>.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Paths to proxy from your apex</label>
                <div className="space-y-2">
                  {PROXY_PATHS.map((p) => {
                    const checked = enabledPaths.includes(p.path);
                    return (
                      <label
                        key={p.path}
                        className={cn(
                          'flex items-start gap-2 p-2.5 rounded-md border cursor-pointer transition-colors',
                          checked ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300',
                          !isVerified && 'opacity-60 cursor-not-allowed'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePath(p.path)}
                          disabled={!isVerified}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-sm">{p.label}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{p.description}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {hostKind && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium">
                      Snippet for <code className="font-mono">{hostInfo?.filename}</code>
                    </label>
                    <button
                      type="button"
                      onClick={handleCopy}
                      disabled={!snippet}
                      className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-50"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </button>
                  </div>
                  <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all">
                    {snippet}
                  </pre>
                  <p className="text-xs text-slate-500 mt-1.5">
                    Paste the snippet, deploy your site, then click Verify below.
                  </p>
                </div>
              )}

              {verifyResults && (
                <div>
                  <div className="text-sm font-medium mb-2">Verify results</div>
                  <div className="space-y-1.5">
                    {verifyResults.map((r) => (
                      <div
                        key={r.path}
                        className={cn(
                          'flex items-start gap-2 p-2 rounded-md text-sm',
                          r.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                        )}
                      >
                        {r.ok ? (
                          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-xs">{r.probedPath}</div>
                          {!r.ok && r.reason && (
                            <div className="text-xs mt-0.5 break-words">{r.reason}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{success}</span>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isVerified || isSaving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save configuration
                </button>
                <button
                  type="button"
                  onClick={handleVerify}
                  disabled={!isVerified || isVerifying || !isConfigured}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                >
                  {isVerifying && <Loader2 className="w-4 h-4 animate-spin" />}
                  Verify apex
                </button>
                {verifiedAt && (
                  <span className="inline-flex items-center text-xs text-slate-500 px-2">
                    Last verified: {new Date(verifiedAt).toLocaleString()}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
